// Story 2.2, 2.6, 3.1, 3.2, 3.4, 3.5: Mileage Entry form modal (create + edit)
import { useState, useEffect, useCallback, useMemo } from 'react';
import { X, ChevronRight, AlertTriangle } from 'lucide-react';
import {
  useCreateMileage,
  useUpdateMileage,
  useMileageEntries,
  useMileageCalculate,
  useMileageRates,
  usePettyCashEntries,
  type CreateMileageInput,
  type MileageBreakdownPreview,
} from '../../hooks/useClaims';
import { MileageBreakdown, MileageBreakdownSkeleton } from './MileageBreakdown';
import { ApiError } from '../../lib/api';
import { LocationAutocomplete, saveRecentDestination } from './LocationAutocomplete';
import { AnimatedModal } from './AnimatedModal';
import { PettyCashFormModal } from './PettyCashFormModal';
import { useUserLocations } from '../../hooks/useUserLocations';

interface MileageFormModalProps {
  claimId: string;
  entryId?: string;
  readOnly?: boolean;
  onClose: () => void;
}

function today(): string {
  return new Date().toISOString().split('T')[0]!;
}

type LocationType = 'HOME' | 'OFFICE' | 'OTHER';

const LOCATION_LABELS: Record<LocationType, string> = {
  HOME: 'Home',
  OFFICE: 'Office',
  OTHER: 'Other',
};

function formatPettyOption(entry: { date: string; description: string; amount: number | string }): string {
  const date = entry.date.split('T')[0] ?? entry.date;
  const amount = Number(entry.amount).toFixed(2);
  return `${date} — ${entry.description} (RM ${amount})`;
}

export function MileageFormModal({ claimId, entryId, readOnly = false, onClose }: MileageFormModalProps) {
  const isEditing = !!entryId;
  const createMileage = useCreateMileage(claimId);
  const updateMileage = useUpdateMileage(claimId);
  const calculate = useMileageCalculate();
  const { data: entriesData } = useMileageEntries(claimId);
  const { data: ratesData } = useMileageRates();
  const { data: pettyCashData } = usePettyCashEntries(claimId);
  const { data: userLocationsData } = useUserLocations();

  const userLocations = useMemo(() => userLocationsData?.locations ?? [], [userLocationsData]);
  const homeLocations = useMemo(() => userLocations.filter((l) => l.locationType === 'HOME'), [userLocations]);
  const officeLocations = useMemo(() => userLocations.filter((l) => l.locationType === 'OFFICE'), [userLocations]);
  const otherLocations = useMemo(() => userLocations.filter((l) => l.locationType === 'OTHER'), [userLocations]);

  const getSavedLocationsForType = useCallback(
    (type: LocationType) => {
      if (type === 'HOME') return homeLocations;
      if (type === 'OFFICE') return officeLocations;
      return otherLocations.length > 0 ? otherLocations : userLocations;
    },
    [homeLocations, officeLocations, otherLocations, userLocations],
  );

  const existingEntry = entriesData?.entries.find((e) => e.id === entryId);

  const [form, setForm] = useState<CreateMileageInput>({
    date: today(),
    origin: '',
    destination: '',
    purpose: '',
    vehicleType: 'Car',
    originType: 'OTHER',
    destType: 'OTHER',
    isRoundTrip: false,
    distanceOverride: undefined,
    overrideReason: '',
    parkingEntryId: null,
    tollEntryId: null,
    userLocationId: null,
  });

  const [originLocId, setOriginLocId] = useState<string>('');
  const [destLocId, setDestLocId] = useState<string>('');

  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [preview, setPreview] = useState<MileageBreakdownPreview | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [viewingPettyCashEntryId, setViewingPettyCashEntryId] = useState<string | null>(null);

  // Pre-fill when editing
  useEffect(() => {
    if (existingEntry) {
      setForm({
        date: existingEntry.date.split('T')[0]!,
        origin: existingEntry.origin,
        destination: existingEntry.destination,
        purpose: existingEntry.purpose ?? '',
        vehicleType: existingEntry.vehicleType ?? 'Car',
        originType: (existingEntry.originType as LocationType) ?? 'OTHER',
        destType: (existingEntry.destType as LocationType) ?? 'OTHER',
        isRoundTrip: existingEntry.isRoundTrip,
        distanceOverride: existingEntry.distanceOverride ? Number(existingEntry.distanceOverride) : undefined,
        overrideReason: existingEntry.overrideReason ?? '',
        parkingEntryId: existingEntry.parkingEntryId ?? null,
        tollEntryId: existingEntry.tollEntryId ?? null,
        userLocationId: existingEntry.userLocationId ?? null,
      });

      if (existingEntry.originType === 'HOME' && existingEntry.userLocationId) {
        setOriginLocId(existingEntry.userLocationId);
      }
      if (existingEntry.destType === 'HOME' && existingEntry.userLocationId) {
        setDestLocId(existingEntry.userLocationId);
      }
    }
  }, [existingEntry]);

  // Parking / Toll options: same-claim entries of that category, not linked to another mileage
  const linkedParkingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of entriesData?.entries ?? []) {
      if (m.parkingEntryId && m.id !== entryId) ids.add(m.parkingEntryId);
    }
    return ids;
  }, [entriesData?.entries, entryId]);

  const linkedTollIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of entriesData?.entries ?? []) {
      if (m.tollEntryId && m.id !== entryId) ids.add(m.tollEntryId);
    }
    return ids;
  }, [entriesData?.entries, entryId]);

  const parkingOptions = useMemo(
    () =>
      (pettyCashData?.entries ?? []).filter(
        (e) => e.category?.name === 'Parking' && !linkedParkingIds.has(e.id),
      ),
    [pettyCashData?.entries, linkedParkingIds],
  );

  const tollOptions = useMemo(
    () =>
      (pettyCashData?.entries ?? []).filter(
        (e) => e.category?.name === 'Toll' && !linkedTollIds.has(e.id),
      ),
    [pettyCashData?.entries, linkedTollIds],
  );

  // Live preview: trigger calculation when both addresses are present
  const triggerPreview = useCallback(
    async (currentForm: typeof form) => {
      if (!currentForm.origin?.trim() || !currentForm.destination?.trim()) {
        setPreview(null);
        return;
      }
      setIsCalculating(true);
      try {
        const result = await calculate.mutateAsync({
          origin: currentForm.origin.trim(),
          destination: currentForm.destination.trim(),
          isRoundTrip: currentForm.isRoundTrip ?? false,
          vehicleType: currentForm.vehicleType ?? 'Car',
          originType: currentForm.originType ?? 'OTHER',
          destType: currentForm.destType ?? 'OTHER',
          distanceOverride: currentForm.distanceOverride,
          userLocationId: currentForm.userLocationId || undefined,
        });
        setPreview(result);
      } catch {
        setPreview(null);
      } finally {
        setIsCalculating(false);
      }
    },
    [calculate],
  );

  // Debounced preview trigger on form change
  useEffect(() => {
    if (readOnly) return;
    const timer = setTimeout(() => {
      void triggerPreview(form);
    }, 800);
    return () => clearTimeout(timer);
  }, [readOnly, form.origin, form.destination, form.isRoundTrip, form.vehicleType, form.originType, form.destType, form.distanceOverride, form.userLocationId]);

  const validate = (): boolean => {
    const errs: typeof errors = {};
    if (!form.date) errs['date'] = 'Date is required';
    if (form.date && new Date(form.date) > new Date()) errs['date'] = 'Date cannot be in the future';
    if (!form.origin?.trim()) errs['origin'] = 'Origin is required';
    if (!form.destination?.trim()) errs['destination'] = 'Destination is required';
    if (form.distanceOverride != null && !form.overrideReason?.trim()) {
      errs['overrideReason'] = 'Reason required when overriding distance';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    if (!validate()) return;
    setSubmitError(null);

    try {
      const payload: CreateMileageInput = {
        date: form.date!,
        origin: form.origin!.trim(),
        destination: form.destination!.trim(),
        purpose: form.purpose?.trim() || undefined,
        vehicleType: form.vehicleType?.trim() || 'Car',
        originType: form.originType ?? 'OTHER',
        destType: form.destType ?? 'OTHER',
        isRoundTrip: form.isRoundTrip,
        distanceOverride: form.distanceOverride,
        overrideReason: form.overrideReason?.trim() || undefined,
        parkingEntryId: form.parkingEntryId || null,
        tollEntryId: form.tollEntryId || null,
        userLocationId: form.userLocationId || null,
      };

      if (isEditing) {
        await updateMileage.mutateAsync({ entryId: entryId!, data: payload });
      } else {
        await createMileage.mutateAsync(payload);
      }
      saveRecentDestination(payload.origin);
      saveRecentDestination(payload.destination);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else {
        setSubmitError('An error occurred. Please try again.');
      }
    }
  };

  const isLoading = createMileage.isPending || updateMileage.isPending;

  const vehicleTypes = ratesData ? Object.keys(ratesData.rates) : ['Car', 'Motorcycle', 'Other'];

  return (
    <>
      <AnimatedModal onClose={onClose}>
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-[17px] font-semibold">
            {readOnly ? 'Mileage Entry Details' : isEditing ? 'Edit Mileage Entry' : 'Add Mileage'}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex h-[30px] w-[30px] items-center justify-center rounded-md text-text-2 transition-colors hover:bg-surface-2 hover:text-text"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {submitError && (
            <div className="rounded-md border border-danger/20 bg-danger-soft px-4 py-3 text-sm text-danger">
              {submitError}
            </div>
          )}

          {/* Date */}
          <div>
            <label className="mb-1.5 block text-[13px] font-semibold" htmlFor="mileage-date">
              Date <span className="text-danger">*</span>
            </label>
            <input
              id="mileage-date"
              type="date"
              max={today()}
              value={form.date}
              readOnly={readOnly}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
            />
            {errors['date'] && <p className="mt-1 text-xs text-danger">{errors['date']}</p>}
          </div>

          {/* Origin + location type */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[13px] font-semibold" htmlFor="mileage-origin">
                From <span className="text-danger">*</span>
              </label>
              {(() => {
                const locs = getSavedLocationsForType(form.originType || 'OTHER');
                if (locs.length === 0 || readOnly) return null;
                return (
                  <div className="flex items-center gap-1.5 text-xs text-text-2">
                    <span>{form.originType === 'HOME' ? 'Base:' : 'Saved:'}</span>
                    <select
                      value={originLocId}
                      onChange={(e) => {
                        const locId = e.target.value;
                        setOriginLocId(locId);
                        const selectedLoc = locs.find((l) => l.id === locId);
                        if (selectedLoc) {
                          setForm((f) => ({
                            ...f,
                            origin: selectedLoc.address,
                            userLocationId: selectedLoc.locationType === 'HOME' ? selectedLoc.id : f.userLocationId,
                          }));
                        }
                      }}
                      className="max-w-[170px] truncate rounded border border-input bg-surface px-1.5 py-0.5 text-xs focus:ring-1 focus:outline-primary"
                    >
                      <option value="">Choose saved location...</option>
                      {locs.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.label} {loc.isDefault ? '(Default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })()}
            </div>
            <div className="grid grid-cols-[112px_1fr] gap-2">
              <select
                id="mileage-origin-type"
                value={form.originType}
                disabled={readOnly}
                onChange={(e) => {
                  const newType = e.target.value as LocationType;
                  let newOrigin = form.origin;
                  let newLocId = form.userLocationId;
                  let newOriginLocId = '';

                  if (newType === 'HOME') {
                    const defaultHome = homeLocations.find((l) => l.isDefault) || homeLocations[0];
                    if (defaultHome) {
                      newOrigin = defaultHome.address;
                      newLocId = defaultHome.id;
                      newOriginLocId = defaultHome.id;
                    }
                  } else if (newType === 'OFFICE') {
                    const defaultOffice = officeLocations.find((l) => l.isDefault) || officeLocations[0];
                    if (defaultOffice) {
                      newOrigin = defaultOffice.address;
                      newOriginLocId = defaultOffice.id;
                    }
                  } else if (newType === 'OTHER') {
                    const defaultOther = otherLocations.find((l) => l.isDefault) || otherLocations[0];
                    if (defaultOther) {
                      newOrigin = defaultOther.address;
                      newOriginLocId = defaultOther.id;
                    }
                  }

                  setOriginLocId(newOriginLocId);
                  setForm((f) => ({
                    ...f,
                    originType: newType,
                    origin: newOrigin || '',
                    userLocationId: newLocId,
                  }));
                }}
                className="h-10 rounded-md border border-input bg-surface px-2 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
              >
                {(Object.keys(LOCATION_LABELS) as LocationType[]).map((lt) => (
                  <option key={lt} value={lt}>{LOCATION_LABELS[lt]}</option>
                ))}
              </select>
              {readOnly ? (
                <input
                  id="mileage-origin"
                  value={form.origin || ''}
                  readOnly
                  className="h-10 flex-1 rounded-md border border-input bg-surface-2/30 px-3 text-sm"
                />
              ) : (
                <LocationAutocomplete
                  id="mileage-origin"
                  placeholder="Address or place name"
                  value={form.origin || ''}
                  onChange={(val) => {
                    setOriginLocId('');
                    setForm((f) => ({ ...f, origin: val }));
                  }}
                  className="h-10 flex-1 rounded-md border border-input bg-surface px-3 text-sm placeholder:text-text-2 focus:outline-2 focus:outline-primary focus:outline-offset-1"
                />
              )}
            </div>
            {errors['origin'] && <p className="mt-1 text-xs text-danger">{errors['origin']}</p>}
          </div>

          {/* Destination + location type */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[13px] font-semibold" htmlFor="mileage-dest">
                To <span className="text-danger">*</span>
              </label>
              {(() => {
                const locs = getSavedLocationsForType(form.destType || 'OTHER');
                if (locs.length === 0 || readOnly) return null;
                return (
                  <div className="flex items-center gap-1.5 text-xs text-text-2">
                    <span>{form.destType === 'HOME' ? 'Base:' : 'Saved:'}</span>
                    <select
                      value={destLocId}
                      onChange={(e) => {
                        const locId = e.target.value;
                        setDestLocId(locId);
                        const selectedLoc = locs.find((l) => l.id === locId);
                        if (selectedLoc) {
                          setForm((f) => ({
                            ...f,
                            destination: selectedLoc.address,
                            userLocationId: (selectedLoc.locationType === 'HOME' && f.originType !== 'HOME') ? selectedLoc.id : f.userLocationId,
                          }));
                        }
                      }}
                      className="max-w-[170px] truncate rounded border border-input bg-surface px-1.5 py-0.5 text-xs focus:ring-1 focus:outline-primary"
                    >
                      <option value="">Choose saved location...</option>
                      {locs.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.label} {loc.isDefault ? '(Default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })()}
            </div>
            <div className="grid grid-cols-[112px_1fr] gap-2">
              <select
                id="mileage-dest-type"
                value={form.destType}
                disabled={readOnly}
                onChange={(e) => {
                  const newType = e.target.value as LocationType;
                  let newDest = form.destination;
                  let newLocId = form.userLocationId;
                  let newDestLocId = '';

                  if (newType === 'HOME') {
                    const defaultHome = homeLocations.find((l) => l.isDefault) || homeLocations[0];
                    if (defaultHome) {
                      newDest = defaultHome.address;
                      newDestLocId = defaultHome.id;
                      if (form.originType !== 'HOME') {
                        newLocId = defaultHome.id;
                      }
                    }
                  } else if (newType === 'OFFICE') {
                    const defaultOffice = officeLocations.find((l) => l.isDefault) || officeLocations[0];
                    if (defaultOffice) {
                      newDest = defaultOffice.address;
                      newDestLocId = defaultOffice.id;
                    }
                  } else if (newType === 'OTHER') {
                    const defaultOther = otherLocations.find((l) => l.isDefault) || otherLocations[0];
                    if (defaultOther) {
                      newDest = defaultOther.address;
                      newDestLocId = defaultOther.id;
                    }
                  }

                  setDestLocId(newDestLocId);
                  setForm((f) => ({
                    ...f,
                    destType: newType,
                    destination: newDest || '',
                    userLocationId: newLocId,
                  }));
                }}
                className="h-10 rounded-md border border-input bg-surface px-2 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
              >
                {(Object.keys(LOCATION_LABELS) as LocationType[]).map((lt) => (
                  <option key={lt} value={lt}>{LOCATION_LABELS[lt]}</option>
                ))}
              </select>
              {readOnly ? (
                <input
                  id="mileage-dest"
                  value={form.destination || ''}
                  readOnly
                  className="h-10 flex-1 rounded-md border border-input bg-surface-2/30 px-3 text-sm"
                />
              ) : (
                <LocationAutocomplete
                  id="mileage-dest"
                  placeholder="Address or place name"
                  value={form.destination || ''}
                  onChange={(val) => {
                    setDestLocId('');
                    setForm((f) => ({ ...f, destination: val }));
                  }}
                  className="h-10 flex-1 rounded-md border border-input bg-surface px-3 text-sm placeholder:text-text-2 focus:outline-2 focus:outline-primary focus:outline-offset-1"
                />
              )}
            </div>
            {errors['destination'] && <p className="mt-1 text-xs text-danger">{errors['destination']}</p>}
          </div>

          {/* Purpose */}
          <div>
            <label className="mb-1.5 block text-[13px] font-semibold" htmlFor="mileage-purpose">
              Purpose
            </label>
            <input
              id="mileage-purpose"
              type="text"
              placeholder="e.g. Client meeting"
              value={form.purpose}
              readOnly={readOnly}
              onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}
              className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm placeholder:text-text-2 focus:outline-2 focus:outline-primary focus:outline-offset-1"
            />
          </div>

          {/* Vehicle + Round Trip */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="mb-1.5 block text-[13px] font-semibold" htmlFor="mileage-vehicle">
                Vehicle
              </label>
              <select
                id="mileage-vehicle"
                value={form.vehicleType}
                disabled={readOnly}
                onChange={(e) => setForm((f) => ({ ...f, vehicleType: e.target.value }))}
                className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
              >
                {vehicleTypes.map((vt) => (
                  <option key={vt} value={vt}>
                    {vt} {ratesData?.rates[vt] != null ? `(RM ${ratesData.rates[vt]?.toFixed(2)}/km)` : ''}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm">
              <input
                id="mileage-round-trip"
                type="checkbox"
                checked={form.isRoundTrip}
                disabled={readOnly}
                onChange={(e) => setForm((f) => ({ ...f, isRoundTrip: e.target.checked }))}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              Round trip
            </label>
          </div>

          {/* Optional Parking / Toll links */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-[13px] font-semibold" htmlFor="mileage-parking">
                Linked Parking <span className="font-normal text-text-2">(optional)</span>
              </label>
              {readOnly ? (
                <button
                  id="mileage-parking"
                  type="button"
                  disabled={!form.parkingEntryId}
                  onClick={() => form.parkingEntryId && setViewingPettyCashEntryId(form.parkingEntryId)}
                  className="flex h-10 w-full items-center rounded-md border border-input bg-surface-2/30 px-3 text-left text-sm enabled:cursor-pointer enabled:hover:bg-surface-2 disabled:cursor-default"
                  aria-label={form.parkingEntryId ? 'View linked parking entry' : 'No linked parking entry'}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {parkingOptions.find((entry) => entry.id === form.parkingEntryId)
                      ? formatPettyOption(parkingOptions.find((entry) => entry.id === form.parkingEntryId)!)
                      : 'None'}
                  </span>
                  {form.parkingEntryId && <ChevronRight className="ml-2 h-4 w-4 shrink-0 text-text-2" />}
                </button>
              ) : (
                <select
                  id="mileage-parking"
                  value={form.parkingEntryId ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, parkingEntryId: e.target.value || null }))}
                  className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
                >
                  <option value="">None</option>
                  {parkingOptions.map((e) => (
                    <option key={e.id} value={e.id}>{formatPettyOption(e)}</option>
                  ))}
                </select>
              )}
              {parkingOptions.length === 0 && (
                <p className="mt-1 text-[11px] text-text-2">
                  Add a Parking petty cash entry on this claim first.
                </p>
              )}
            </div>
            <div>
              <label className="mb-1.5 block text-[13px] font-semibold" htmlFor="mileage-toll">
                Linked Toll <span className="font-normal text-text-2">(optional)</span>
              </label>
              {readOnly ? (
                <button
                  id="mileage-toll"
                  type="button"
                  disabled={!form.tollEntryId}
                  onClick={() => form.tollEntryId && setViewingPettyCashEntryId(form.tollEntryId)}
                  className="flex h-10 w-full items-center rounded-md border border-input bg-surface-2/30 px-3 text-left text-sm enabled:cursor-pointer enabled:hover:bg-surface-2 disabled:cursor-default"
                  aria-label={form.tollEntryId ? 'View linked toll entry' : 'No linked toll entry'}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {tollOptions.find((entry) => entry.id === form.tollEntryId)
                      ? formatPettyOption(tollOptions.find((entry) => entry.id === form.tollEntryId)!)
                      : 'None'}
                  </span>
                  {form.tollEntryId && <ChevronRight className="ml-2 h-4 w-4 shrink-0 text-text-2" />}
                </button>
              ) : (
                <select
                  id="mileage-toll"
                  value={form.tollEntryId ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, tollEntryId: e.target.value || null }))}
                  className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
                >
                  <option value="">None</option>
                  {tollOptions.map((e) => (
                    <option key={e.id} value={e.id}>{formatPettyOption(e)}</option>
                  ))}
                </select>
              )}
              {tollOptions.length === 0 && (
                <p className="mt-1 text-[11px] text-text-2">
                  Add a Toll petty cash entry on this claim first.
                </p>
              )}
            </div>
          </div>

          {/* Live calculation preview */}
          {readOnly && existingEntry ? (
            <div>
              <p className="mb-1.5 text-xs font-medium text-text-2">Calculation Breakdown</p>
              <MileageBreakdown
                origin={existingEntry.origin}
                destination={existingEntry.destination}
                vehicleType={existingEntry.vehicleType}
                breakdown={{
                  totalDistanceKm: Number(existingEntry.distanceOverride ?? existingEntry.distance) * (existingEntry.isRoundTrip ? 2 : 1),
                  homeOfficeDeductionKm: Number(existingEntry.homeOfficeDeduction),
                  claimableDistanceKm: Number(existingEntry.claimableDistance),
                  ratePerKm: Number(existingEntry.rateUsed ?? 0),
                  amountMyr: Number(existingEntry.amount),
                  deductionApplied: Number(existingEntry.homeOfficeDeduction) > 0,
                  deductionExplanation: 'Home-office travel deduction applied.',
                }}
                hasOverride={existingEntry.distanceOverride != null}
                overrideReason={existingEntry.overrideReason}
              />
            </div>
          ) : (form.origin?.trim() && form.destination?.trim()) && (
            <div>
              <p className="mb-1.5 text-xs font-medium text-text-2">Calculation Preview</p>
              {isCalculating ? (
                <MileageBreakdownSkeleton />
              ) : preview ? (
                <MileageBreakdown
                  origin={preview.resolvedOrigin}
                  destination={preview.resolvedDestination}
                  vehicleType={preview.vehicleType}
                  breakdown={preview.breakdown}
                  hasOverride={!!form.distanceOverride}
                  overrideReason={form.overrideReason}
                />
              ) : null}
              {!preview?.mapsAvailable && !isCalculating && (
                <p className="mt-1 flex items-center gap-1.5 text-xs text-warn">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                  Auto-distance unavailable — enter a manual distance below or save to use configured default.
                </p>
              )}
            </div>
          )}

          {/* Distance Override */}
          <details className="group" open={readOnly && form.distanceOverride !== undefined ? true : undefined}>
            <summary className="flex list-none cursor-pointer items-center gap-1 text-[13px] font-semibold">
              <ChevronRight
                className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90"
                strokeWidth={2.5}
              />
              Override distance manually
            </summary>
            <div className="mt-2.5 space-y-2">
              <input
                id="mileage-distance"
                type="number"
                step="0.1"
                min="0"
                placeholder="Distance in km"
                value={form.distanceOverride ?? ''}
                readOnly={readOnly}
                onChange={(e) => setForm((f) => ({ ...f, distanceOverride: e.target.value ? parseFloat(e.target.value) : undefined }))}
                className="h-10 w-full rounded-md border border-input bg-surface px-3 font-mono text-sm placeholder:text-text-2 focus:outline-2 focus:outline-primary focus:outline-offset-1"
              />
              {form.distanceOverride !== undefined && (
                <div>
                  <input
                    id="mileage-override-reason"
                    type="text"
                    placeholder="Reason for manual distance (required)"
                    value={form.overrideReason}
                    readOnly={readOnly}
                    onChange={(e) => setForm((f) => ({ ...f, overrideReason: e.target.value }))}
                    className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm placeholder:text-text-2 focus:outline-2 focus:outline-primary focus:outline-offset-1"
                  />
                  {errors['overrideReason'] && <p className="mt-1 text-xs text-danger">{errors['overrideReason']}</p>}
                </div>
              )}
            </div>
          </details>

          {/* Submit */}
          <div className="mt-2 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="h-10 rounded-md border border-border px-[18px] text-sm font-medium transition-colors hover:bg-surface-2"
            >
              {readOnly ? 'Close' : 'Cancel'}
            </button>
            {!readOnly && (
              <button
                id="mileage-submit-btn"
                type="submit"
                disabled={isLoading}
                className="h-10 rounded-md bg-primary px-[18px] text-sm font-medium text-white transition-colors hover:bg-primary-hover disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Entry'}
              </button>
            )}
          </div>
          {!readOnly && (
            <p className="text-right text-xs text-text-2">
              Rate is calculated automatically from your vehicle and distance.
            </p>
          )}
        </form>
      </AnimatedModal>
      {viewingPettyCashEntryId && (
        <PettyCashFormModal
          claimId={claimId}
          entryId={viewingPettyCashEntryId}
          readOnly
          onClose={() => setViewingPettyCashEntryId(null)}
        />
      )}
    </>
  );
}
